import { ER_STAKEHOLDER_TYPES, STAKEHOLDER_OPTIONS } from "./model";
import {
  STAKEHOLDER_COMMANDER,
  STAKEHOLDER_EMERGENCY_RESPONDER,
} from "./stakeholderConstants";

/** Values shown on the registration "Are you a:" dropdown (subset of all CRM stakeholder types). */
export const REGISTRATION_FORM_STAKEHOLDER_VALUES: readonly string[] = STAKEHOLDER_OPTIONS.map(
  (o) => o.value,
);

/**
 * Job title → role bucket (parity with buxusweb `ROLE_MAP`).
 * Commander titles (including Trainer) promote stakeholder to `EC - Commander`
 * for wizard screens. A Yes on the unaffiliated professional ER trainer question
 * overrides the *stored* CRM type to ER without changing the screen path.
 */
type RoleBucket = "Commander" | "Emergency Responder" | "Member of the Public";

export const ROLE_MAP: ReadonlyArray<Record<string, RoleBucket>> = [
  { "1st Deputy Fire": "Commander" },
  { "Assistant Chief": "Commander" },
  { "Assistant Deputy Chief": "Commander" },
  { "Assistant Deputy Fire": "Commander" },
  { "Battalion Chief": "Commander" },
  { Captain: "Commander" }, // eslint-disable-line quote-props -- valid identifier
  { "Chief of Police": "Commander" },
  { Commissioner: "Commander" },
  { "Communications Officer": "Emergency Responder" },
  { Constable: "Commander" },
  { Coordinator: "Emergency Responder" },
  { Corporal: "Commander" },
  { "Deputy District Chief": "Commander" },
  { "Deputy Fire": "Commander" },
  { "Deputy Police Chief": "Commander" },
  { "Deputy Sheriff": "Commander" },
  { Dispatcher: "Emergency Responder" },
  { "District Chief": "Commander" },
  { "Driver Engineer": "Emergency Responder" },
  { Driver: "Emergency Responder" },
  { "Emergency Management": "Commander" },
  { EMT: "Emergency Responder" },
  { "Fire Chief": "Commander" },
  { "Fire Commissioner": "Commander" },
  { "Fire Marshal": "Commander" },
  { Firefighter: "Emergency Responder" },
  { HAZMAT: "Commander" },
  { Inspector: "Commander" },
  { Lieutenant: "Commander" },
  { Major: "Commander" },
  { "Member of Public": "Member of the Public" },
  // App-only fallback: CRM Job_Title has no "Other" picklist value. Unused Zoho field
  // Job_Title_Other_Description is unrelated and not wired in registration/ABP models.
  { Other: "Emergency Responder" },
  { Paramedic: "Emergency Responder" },
  { "Police Chief": "Commander" },
  { "Police Officer": "Emergency Responder" },
  { Sergeant: "Commander" },
  { Sheriff: "Commander" },
  { Superintendent: "Commander" },
  { Trainer: "Commander" },
  { "Volunteer Firefighter": "Emergency Responder" },
] as const;

export const JOB_TITLE_OPTIONS = ROLE_MAP.map((row) => Object.keys(row)[0]!).filter(
  (title) => title !== "Member of Public",
);

export const DISPATCHER_JOB_TITLE = "Dispatcher";

export const TRAINER_JOB_TITLE = "Trainer";

export const COMMUNICATIONS_OFFICER_JOB_TITLE = "Communications Officer";

/** Job titles that open Dispatch Capabilities and require Communications Role. */
export const DISPATCH_TRIGGER_JOB_TITLES = [
  COMMUNICATIONS_OFFICER_JOB_TITLE,
  DISPATCHER_JOB_TITLE,
] as const;

/** Shown when job title is Dispatcher or Communications Officer (Zoho `Communications_Role`). */
export const COMMUNICATIONS_ROLE_OPTIONS = [
  "Call Taker",
  "Radio Operator",
  "Telecommunications Officer",
] as const;

/** Agency types that trigger the Dispatch Capabilities step (after Agency Info). */
export const DISPATCH_AGENCY_TYPES = [
  "Emergency Dispatch",
  "Non-Emergency Dispatch (Secondary)",
  "Primary PSAP",
] as const;

export function roleBucketForJobTitle(jobTitle: string): "Commander" | "Emergency Responder" | null {
  const hit = ROLE_MAP.find((row) => Object.keys(row)[0] === jobTitle.trim());
  const bucket = hit ? Object.values(hit)[0] : null;
  if (bucket === "Commander" || bucket === "Emergency Responder") return bucket;
  return null;
}

/**
 * Map CRM `Stakeholder_Type` to the registration "Are you a:" dropdown value.
 * ET / EC / ER are not all listed in the dropdown — collapse the ER family to Emergency Responder
 * (job title can still promote EC on submit via `resolveRegistrationStakeholderType`).
 */
export function mapCrmStakeholderTypeToRegistrationFormSelect(
  crmStakeholderType: string | null | undefined,
): string | undefined {
  const trimmed = crmStakeholderType?.trim();
  if (!trimmed) return undefined;
  if (REGISTRATION_FORM_STAKEHOLDER_VALUES.includes(trimmed)) return trimmed;
  if (ER_STAKEHOLDER_TYPES.includes(trimmed as (typeof ER_STAKEHOLDER_TYPES)[number])) {
    return STAKEHOLDER_EMERGENCY_RESPONDER;
  }
  return undefined;
}

/**
 * Wizard-path stakeholder type: Commander job titles (including Trainer) promote
 * ER to `EC - Commander`. Does not apply the unaffiliated-trainer CRM override.
 */
export function resolveRegistrationWizardStakeholderType(
  stakeholderType: string,
  jobTitle: string,
): string {
  if (stakeholderType !== STAKEHOLDER_EMERGENCY_RESPONDER) {
    return stakeholderType;
  }
  const trimmed = jobTitle.trim();
  if (!trimmed) return stakeholderType;
  const bucket = roleBucketForJobTitle(trimmed);
  if (bucket === "Commander") return STAKEHOLDER_COMMANDER;
  return STAKEHOLDER_EMERGENCY_RESPONDER;
}

/**
 * CRM stakeholder type. Same as the wizard path, except a Yes on the unaffiliated
 * professional ER trainer question stores `ER - Emergency Responder` regardless
 * of job title (and does not change which registration screens appear).
 */
export function resolveRegistrationStakeholderType(
  stakeholderType: string,
  jobTitle: string,
  isUnaffiliatedProfessionalErTrainer?: string | null,
): string {
  if (
    stakeholderType === STAKEHOLDER_EMERGENCY_RESPONDER &&
    isUnaffiliatedProfessionalErTrainerAnswer(isUnaffiliatedProfessionalErTrainer)
  ) {
    return STAKEHOLDER_EMERGENCY_RESPONDER;
  }
  return resolveRegistrationWizardStakeholderType(stakeholderType, jobTitle);
}

/** Maps job title to CRM stakeholder type when user picks an ER-style job title. */
export function stakeholderTypeFromJobTitle(jobTitle: string): string | null {
  const bucket = roleBucketForJobTitle(jobTitle);
  if (bucket === "Commander") return STAKEHOLDER_COMMANDER;
  if (bucket === "Emergency Responder") return STAKEHOLDER_EMERGENCY_RESPONDER;
  return null;
}

export function isTrainerJobTitle(jobTitle: string | null | undefined): boolean {
  return jobTitle?.trim() === TRAINER_JOB_TITLE;
}

export function isUnaffiliatedProfessionalErTrainerAnswer(
  value: string | null | undefined,
): boolean {
  return value?.trim() === "Yes";
}

export function isDispatcherJobTitle(jobTitle: string | null | undefined): boolean {
  const trimmed = jobTitle?.trim();
  return (
    !!trimmed &&
    DISPATCH_TRIGGER_JOB_TITLES.includes(trimmed as (typeof DISPATCH_TRIGGER_JOB_TITLES)[number])
  );
}

export function requiresCommunicationsRoleJobTitle(jobTitle: string | null | undefined): boolean {
  return isDispatcherJobTitle(jobTitle);
}

export function includesDispatchAgencyType(
  typeOfAgency: readonly string[] | string | null | undefined,
): boolean {
  if (!typeOfAgency) return false;
  const values = Array.isArray(typeOfAgency) ? typeOfAgency : [typeOfAgency];
  return values.some((v) =>
    DISPATCH_AGENCY_TYPES.includes(v.trim() as (typeof DISPATCH_AGENCY_TYPES)[number]),
  );
}
